/**
 * `ActivitySection` — "Agent activity" card. Three panes:
 *   1. AI visits (7 complete days) + sparkline + trend sentence.
 *   2. Top 3 AI agents by traffic.
 *   3. "What they looked at": AI sessions reaching product / search / cart pages (counts).
 *
 * Trend rule from the page spec:
 *   - show "%" only if prior ≥ 30 and current+prior ≥ 60; else "cur → prior".
 *   - |change| < 15% with total < 200 → "about the same".
 */

import type { AgentOverviewResponse } from '@sill/types/api';

import { useC } from '../../lib/theme.js';
import { monoFont, sansFont, serifFont } from './fonts.js';
import { Kicker, MonoLink, Dash } from './MonoLink.js';
import type { SetView } from '../../app/view-router.js';

export interface ActivitySectionProps {
  readonly overview: AgentOverviewResponse | null;
  /** Click on "See all activity" — opens the Agents view default (AI). */
  readonly onNavigate: () => void;
  /** Click on a drill-down — a specific view + filter. */
  readonly setView: SetView;
}

function Sparkline({ values }: { values: readonly number[] }): JSX.Element {
  const C = useC();
  const n = values.length;
  if (n === 0) {
    return <div style={{ width: 140, height: 32 }} aria-hidden="true" />;
  }
  const max = Math.max(1, ...values);
  const pts = values.map((v, i) => {
    const x = (i / Math.max(1, n - 1)) * 138;
    const y = 28 - (v / max) * 20;
    return `${x},${y}`;
  }).join(' ');
  const last = values[n - 1]!;
  const lastY = 28 - (last / max) * 20;
  return (
    <svg width={140} height={32} viewBox="0 0 140 32" aria-label="Daily AI agent visits">
      <polyline points={pts} stroke={C.body} strokeWidth={1.5} fill="none" />
      <circle cx={138} cy={lastY} r={3} fill={C.text} />
    </svg>
  );
}

/** Trend sentence per the page's rule. */
export function trendSentence(curr: number, prior: number): string {
  const total = curr + prior;
  if (total === 0) return 'no visits yet';
  if (prior >= 30 && total >= 60) {
    const pct = Math.round(((curr - prior) / prior) * 100);
    if (Math.abs(pct) < 15 && total < 200) return 'about the same as last week';
    if (pct === 0) return 'same as last week';
    return `${pct > 0 ? 'up' : 'down'} ${Math.abs(pct).toLocaleString('en-US')}% on last week`;
  }
  return `${curr.toLocaleString('en-US')} → ${prior.toLocaleString('en-US')}`;
}

export function ActivitySection({ overview, onNavigate, setView }: ActivitySectionProps): JSX.Element {
  const C = useC();
  const t = overview?.totals;
  const series = overview?.series.agents ?? [];
  const topAgents = series
    .map((a) => ({
      name: a.inferred_name ?? a.agent_id ?? a.agent_key,
      key: a.agent_key,
      total: a.traffic.reduce((s, v) => s + v, 0),
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 3);

  // Daily totals series across all AI agents (sum per day).
  const days = overview?.series.days ?? [];
  const daily: number[] = days.map((_, i) => {
    let sum = 0;
    for (const a of series) sum += a.traffic[i] ?? 0;
    sum += overview?.series.other[i] ?? 0;
    return sum;
  });

  // "What they looked at": AI-agent sessions that reached each kind of
  // page. Counts, not shares: one session can reach several kinds, and the
  // rest of a session's pages are unclassified, so a % would mislead.
  const funnel = overview?.funnel;
  const looked: ReadonlyArray<{ label: string; v: number }> = funnel ? [
    { label: 'Product pages', v: funnel.product },
    { label: 'Search', v: funnel.search },
    { label: 'Cart or checkout', v: funnel.cart + funnel.checkout },
  ] : [];
  const lookedAny = looked.some((b) => b.v > 0);

  return (
    <section
      id="activity"
      aria-label="Agent activity"
      style={{
        background: C.surface,
        border: `1px solid ${C.border}`,
        borderRadius: 4,
        fontFamily: sansFont,
      }}
    >
      <div
        className="flex items-baseline justify-between gap-3"
        style={{ padding: '24px 24px 16px' }}
      >
        <h2 style={{ margin: 0, fontFamily: serifFont, fontWeight: 400, fontSize: 'clamp(22px, 3vw, 28px)', color: C.text }}>
          Agent activity
        </h2>
        <MonoLink onClick={onNavigate} aria-label="See all activity">See all activity</MonoLink>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3" style={{ borderTop: `1px solid ${C.borderSoft}` }}>
        <button
          type="button"
          onClick={() => setView('agents', { agent_kind: 'ai', agents_mode: 'overview' })}
          aria-label="See AI agents"
          style={{
            padding: '22px 24px',
            display: 'flex', flexDirection: 'column', gap: 12,
            borderRight: `1px solid ${C.borderSoft}`,
            background: 'transparent', border: 'none', textAlign: 'left',
            fontFamily: sansFont, color: 'inherit', cursor: 'pointer',
          }}
        >
          <Kicker>AI agent visits, 7 days</Kicker>
          <div className="flex items-end gap-4">
            <span style={{ fontFamily: serifFont, fontSize: 40, color: C.text, lineHeight: 1 }}>
              {t === undefined ? <Dash /> : t.traffic.toLocaleString('en-US')}
            </span>
            <Sparkline values={daily} />
          </div>
          <div style={{ fontFamily: monoFont, fontSize: 12, color: C.muted }}>
            {t === undefined ? '—' : trendSentence(t.traffic, overview?.prior.traffic ?? 0)}
          </div>
        </button>

        <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 10, borderRight: `1px solid ${C.borderSoft}` }}>
          <Kicker>Top AI agents</Kicker>
          {topAgents.length === 0 ? (
            <div style={{ fontSize: 13, color: C.muted }}>No AI agents visited this week.</div>
          ) : (
            topAgents.map((a) => (
              <button
                key={a.key}
                type="button"
                onClick={() => setView('agents', { agent_kind: 'ai', agent: a.key, agents_mode: 'agents' })}
                className="flex justify-between"
                style={{
                  fontSize: 14, textAlign: 'left', width: '100%',
                  background: 'transparent', border: 'none', padding: 0,
                  fontFamily: sansFont, color: 'inherit', cursor: 'pointer',
                }}
                aria-label={`See ${a.name}'s activity`}
              >
                <span style={{ color: C.text, textDecoration: 'underline', textDecorationColor: C.dim, textUnderlineOffset: '3px' }}>{a.name}</span>
                <span style={{ fontFamily: monoFont, color: C.body }}>{a.total.toLocaleString('en-US')}</span>
              </button>
            ))
          )}
        </div>

        <div style={{ padding: '22px 24px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <Kicker>What they looked at</Kicker>
          {funnel === undefined ? (
            <div style={{ fontSize: 13, color: C.muted }}>Run your first check.</div>
          ) : !lookedAny ? (
            <div style={{ fontSize: 13, color: C.muted }}>No product, search or cart pages this week.</div>
          ) : (
            <>
              {looked.map((b) => (
                <button
                  key={b.label}
                  type="button"
                  onClick={() => setView('agents', { agent_kind: 'ai', agents_mode: 'demand' })}
                  className="flex justify-between"
                  style={{
                    fontSize: 14, textAlign: 'left', width: '100%',
                    background: 'transparent', border: 'none', padding: 0,
                    fontFamily: sansFont, color: 'inherit', cursor: 'pointer',
                  }}
                  aria-label={`See demand for ${b.label}`}
                >
                  <span style={{ color: C.text, textDecoration: 'underline', textDecorationColor: C.dim, textUnderlineOffset: '3px' }}>{b.label}</span>
                  <span style={{ fontFamily: monoFont, color: C.body }}>{b.v.toLocaleString('en-US')}</span>
                </button>
              ))}
              <div style={{ fontSize: 12, color: C.muted }}>Sessions by AI agents that reached each kind of page.</div>
            </>
          )}
        </div>
      </div>

      <div style={{ padding: '14px 24px', borderTop: `1px solid ${C.borderSoft}`, fontSize: 12, color: C.muted }}>
        Each page load is counted once, where Sill is installed. Agents that sign their requests are recognised even when they browse as Chrome (Cloudflare Worker and WordPress plugin installs); unsigned agents that browse as Chrome are counted as people.
      </div>
    </section>
  );
}
